import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import { Box, LoadingMask, Modal } from '@zillow/constellation-10';
import type { CreateRenterPaymentMethodWebApiResponseDto } from '@zg-rentals/ts-rent-payment-client';

import { useCreateRenterPaymentMethod } from '@renter-hub/hooks/useCreatePaymentMethod';
import NotificationContainer from '@common/components/NotificationContainer';
import Page from '@renter-hub/components/RenterPage';
import HelmetContainer from '@common/components/HelmetContainer';
import SeoConstants from '@renter-hub/constants/seoConstants';
import PaymentMethod from '@common/constants/PaymentMethod';
import userPersonas from '@common/constants/userPersonas';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
import BillingAddressSection from './modules/common/BillingAddressSection';
import PaymentTypeSelection from './modules/common/PaymentTypeSelection';
import InjectedCardSection from './modules/card/CardSection';
import CancelModal from './modules/CancelModal';
import Header from './modules/common/Header';
import CoFeesDisclaimer from './modules/common/CoFeesDisclaimer';
import FaqSection from './modules/common/FaqSection';
import DuplicatePaymentMethodWarning from './modules/DuplicatePaymentMethodWarning';
import {
  getPageFlow,
  PAGE_BILLING_ADDRESS_ACH,
  PAGE_BILLING_ADDRESS_CARD,
  PAGE_CARD_INPUT,
  PAGE_DUPLICATE_WARNING,
  PAGE_MICRO_DEPOSIT,
  PAGE_PAYMENT_TYPE_SELECTION,
} from './pageFlows';
import type { RenterConfigInterface, RenterPaymentMethodTypeInterface } from '@renter-hub/models/Types';
import { PAYMENT_METHOD } from '@renter-hub/constants/paymentConstants';
import { trackPaymentBack, trackPaymentContinue } from './clickstream';
import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';
import { findDuplicatePaymentMethod } from '@common/paymentsUtils/duplicatePaymentMethodUtil';
import type { RenterPaymentMethodInterface } from '@renter-hub/models/Types';

type FlowPartialUpdate = Partial<{
  plaidToken: any;
  stripeToken: string;
  address: any;
  modeOfPayment: string;
  accountNickname: string;
}>;

interface Props {
  fromDashboard: boolean;
  config: RenterConfigInterface;
  modeOfPayment: string;
  isColoradoCustomer: boolean;
  address?: any;
  onPaymentMethodCreationSuccess: (paymentMethod: CreateRenterPaymentMethodWebApiResponseDto) => any;
  onPaymentMethodCreationError: (error: unknown) => any;
  trackValidationError: (...args: any) => any;
  onCancel: (...args: any) => any;
  paymentMethodTypes: Array<RenterPaymentMethodTypeInterface>;
  user: ZrmClickstreamUser;
  paymentMethods: RenterPaymentMethodInterface[];
  checkForDuplicatePaymentMethod: (mask: string) => any;
  // for storybook
  nextStepForStorybook?: () => void;
  skipFieldValidationForStorybook?: boolean;
  listingAlias?: string;
}

const PaymentInfoPage: React.FC<Props> = (props: Props) => {
  const [accountNickname, setAccountNickname] = useState<string>('');
  const [isStripeLoaded, setIsStripeLoaded] = useState<boolean>(false);
  const [modeOfPayment, setModeOfPayment] = useState<string>(props.modeOfPayment);
  const [address, setAddress] = useState<any>(props.address);
  const [isPendingDataSave, setIsPendingDataSave] = useState<boolean>(false);
  const [cancelModal, setCancelModal] = useState<boolean>(false);
  const [pageFlow, setPageFlow] = useState<Array<string>>(
    getPageFlow(props.fromDashboard, props.modeOfPayment, PaymentMethod.INPUT_TYPE.BANK_PLAID),
  );
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [stripeToken, setStripeToken] = useState<string>('');
  const [plaidToken, setPlaidToken] = useState<any>({});
  const [duplicatePaymentMethod, setDuplicatePaymentMethod] = useState<any>(null);
  const [pendingPaymentInfo, setPendingPaymentInfo] = useState<any>(null);

  useEffect(() => {
    if ((window as any).Stripe) {
      setIsStripeLoaded(true);
    } else {
      const scriptEl: any = (document as any).querySelector('#stripe-js');
      const onLoad = () => setIsStripeLoaded(true);
      scriptEl?.addEventListener('load', onLoad);
    }
  }, []);

  const { mutateAsync: createRenterPaymentMethod } = useCreateRenterPaymentMethod({
    onSuccess: (response) => props.onPaymentMethodCreationSuccess({ paymentMethod: response.paymentMethod }),
    onError: (error) => {
      const message = error instanceof Error ? error.message : undefined;
      props.onPaymentMethodCreationError({ errorMessage: message } as any);
      setIsPendingDataSave(false);
    },
  });

  const applyPartialUpdate = (newState: FlowPartialUpdate = {}) => {
    if (newState.plaidToken !== undefined) setPlaidToken(newState.plaidToken);
    if (newState.stripeToken !== undefined) setStripeToken(newState.stripeToken as string);
    if (newState.address !== undefined) setAddress(newState.address);
    if (newState.modeOfPayment !== undefined) setModeOfPayment(newState.modeOfPayment as string);
    if (newState.accountNickname !== undefined) setAccountNickname(newState.accountNickname as string);
  };

  const onSubmitPaymentInfo = async (newState: FlowPartialUpdate = {}) => {
    setIsPendingDataSave(true);

    applyPartialUpdate(newState);

    if (!props.fromDashboard) {
      RachelCSEvents.emitClickContinueFromAddressPageEvent({
        user: props.user,
      });
    }

    const nextMode = newState.modeOfPayment ?? modeOfPayment;
    const nextAddress = newState.address ?? address;
    const nextPlaidToken = newState.plaidToken ?? plaidToken;
    const nextStripeToken = newState.stripeToken ?? stripeToken;
    const nextAccountNickname = newState.accountNickname ?? accountNickname;

    const isBankAccount = nextMode === PaymentMethod.TYPE.BANK_ACCOUNT;
    const addressInfo = props.fromDashboard && isBankAccount ? null : nextAddress;

    const requestPayload = isBankAccount
      ? {
          plaidToken: {
            accountId: nextPlaidToken.accountId,
            publicToken: nextPlaidToken.publicToken,
          },
          addressInfo,
          bankName: nextPlaidToken.bankName,
          mask: nextPlaidToken.mask,
          ...(nextPlaidToken.status && { status: nextPlaidToken.status }),
        }
      : {
          paymentSourceToken: nextStripeToken,
          addressInfo,
        };

    if (isBankAccount && nextPlaidToken?.mask) {
      const duplicatePaymentMethod = findDuplicatePaymentMethod(
        { mask: nextPlaidToken.mask, bankName: nextPlaidToken.bankName },
        props.paymentMethods,
      );

      if (duplicatePaymentMethod) {
        setDuplicatePaymentMethod(duplicatePaymentMethod);
        setPendingPaymentInfo({
          plaidToken: {
            accountId: nextPlaidToken.accountId,
            publicToken: nextPlaidToken.publicToken,
          },
          bankName: nextPlaidToken.bankName,
          mask: nextPlaidToken.mask,
          ...(nextPlaidToken.status && { status: nextPlaidToken.status }),
        });
        setIsPendingDataSave(false);

        const newFlow = [...pageFlow];
        newFlow.splice(pageIndex + 1, 0, PAGE_DUPLICATE_WARNING);
        setPageFlow(newFlow);

        return { error: false } as const;
      }
    }
    await createRenterPaymentMethod({
      postData: {
        ...requestPayload,
        nickname: nextAccountNickname,
      },
    });

    return { error: false };
  };

  const setPaymentType = useCallback(
    (nextMode: string) => {
      if (modeOfPayment !== nextMode) {
        const infoInputType =
          nextMode === PaymentMethod.TYPE.BANK_ACCOUNT
            ? PaymentMethod.INPUT_TYPE.BANK_PLAID
            : PaymentMethod.INPUT_TYPE.STRIPE_ELEMENT;
        setModeOfPayment(nextMode);
        setPageFlow(getPageFlow(props.fromDashboard, nextMode, infoInputType));
      }
    },
    [modeOfPayment, props.fromDashboard],
  );

  const navigatePage = (direction: 'next' | 'prev', newState: FlowPartialUpdate = {}) => {
    const currentPage = pageFlow[pageIndex];
    if (!props.fromDashboard) {
      if (direction === 'next') {
        trackPaymentContinue(props.user, currentPage, modeOfPayment, props.listingAlias);
      } else {
        trackPaymentBack(props.user, currentPage);
      }
    }
    applyPartialUpdate(newState);
    setPageIndex((idx) => idx + (direction === 'next' ? 1 : -1));
  };

  const handlePlaidSubmit = async (arg: any) => {
    const token = arg?.plaidToken ?? arg;
    if (props.fromDashboard) {
      const result = await onSubmitPaymentInfo({ plaidToken: token });
      if (result && !result.error) {
        navigatePage('next', { plaidToken: token });
      }
    } else {
      return navigatePage('next', { plaidToken: token });
    }
  };

  const renderPaymentTypeSelection = () => (
    <PaymentTypeSelection
      isLoading={isPendingDataSave}
      modeOfPayment={modeOfPayment}
      onSelect={setPaymentType}
      onContinue={(newState) => navigatePage('next', newState)}
      onPlaidSubmit={handlePlaidSubmit}
      paymentMethodTypes={props.paymentMethodTypes}
      fromDashboard={props.fromDashboard}
    />
  );

  const renderStripeElement = () => {
    const {
      config: { stripePublicKey },
    } = props;
    const stripePromise = loadStripe(stripePublicKey);
    return (
      <Elements stripe={stripePromise}>
        <InjectedCardSection
          modeOfPayment={modeOfPayment}
          onBack={() => navigatePage('prev')}
          onContinue={(newState) => navigatePage('next', newState)}
          nextStepForStorybook={props.skipFieldValidationForStorybook ? () => navigatePage('next') : undefined}
        />
      </Elements>
    );
  };

  const renderBillingAddressSection = () => (
    <BillingAddressSection
      isLoading={isPendingDataSave}
      address={address}
      modeOfPayment={modeOfPayment}
      onBack={() => navigatePage('prev')}
      onSubmit={onSubmitPaymentInfo}
      trackValidationError={props.trackValidationError}
      skipFieldValidationForStorybook={props.skipFieldValidationForStorybook}
    />
  );

  const renderDuplicateWarning = () => (
    <DuplicatePaymentMethodWarning
      duplicatePaymentMethod={duplicatePaymentMethod}
      pendingPaymentInfo={pendingPaymentInfo}
      isPendingDataSave={isPendingDataSave}
      setIsPendingDataSave={setIsPendingDataSave}
      onPaymentMethodCreationSuccess={props.onPaymentMethodCreationSuccess}
      onPaymentMethodCreationError={props.onPaymentMethodCreationError}
    />
  );

  const renderMainSection = (renderCOFees = false, isModal = false) => {
    const currentPage = pageFlow[pageIndex];
    return (
      <>
        <NotificationContainer modal={isModal} persona={userPersonas.RENTER} />
        {currentPage === PAGE_PAYMENT_TYPE_SELECTION && renderPaymentTypeSelection()}
        {currentPage === PAGE_CARD_INPUT && renderStripeElement()}
        {currentPage === PAGE_BILLING_ADDRESS_ACH && renderBillingAddressSection()}
        {currentPage === PAGE_BILLING_ADDRESS_CARD && renderBillingAddressSection()}
        {currentPage === PAGE_DUPLICATE_WARNING && renderDuplicateWarning()}
        {renderCOFees && currentPage === PAGE_PAYMENT_TYPE_SELECTION && !isModal && <CoFeesDisclaimer />}
      </>
    );
  };

  const renderPage = () => {
    const progressBarProps = { progressTitle: PAYMENT_METHOD };
    const currentPage = pageFlow[pageIndex];
    return (
      <LoadingMask
        iconSize="lg"
        loading={(isStripeLoaded === false && currentPage === PAGE_CARD_INPUT) || currentPage === PAGE_MICRO_DEPOSIT}
        loadingVoiceOver="Loading"
        role="status"
      >
        <Page {...progressBarProps}>
          <HelmetContainer {...SeoConstants.paymentInfoPage} />
          <Page.MainSection>
            <Header currentPage={currentPage} />
            {renderMainSection(props.isColoradoCustomer)}
          </Page.MainSection>
          <Page.RightSection>
            <FaqSection fromDashboard={props.fromDashboard} currentPage={currentPage} />
          </Page.RightSection>
          <Page.BottomSection>
            <FaqSection fromDashboard={props.fromDashboard} currentPage={currentPage} />
          </Page.BottomSection>
        </Page>
      </LoadingMask>
    );
  };

  const renderModal = () => {
    const currentPage = pageFlow[pageIndex];
    if (cancelModal) {
      return (
        <CancelModal isOpen={cancelModal} toggleIsOpen={() => setCancelModal(false)} goToDashboard={props.onCancel} />
      );
    }
    return (
      <Modal
        trigger={null}
        open={true}
        size="sm"
        onOpenChange={(isOpen) => {
          if (!isOpen) setCancelModal(true);
        }}
        header={
          <Box css={{ padding: 'token(spacing.sm) !important' }}>
            <Header currentPage={currentPage} headerSize="xs" />
          </Box>
        }
        body={
          <LoadingMask
            iconSize="lg"
            loading={
              (isStripeLoaded === false && currentPage === PAGE_CARD_INPUT) || currentPage === PAGE_MICRO_DEPOSIT
            }
            loadingVoiceOver="Loading"
            role="status"
          >
            <Box css={{ padding: 'token(spacing.sm) !important' }}>
              <HelmetContainer {...SeoConstants.paymentInfoPage} />
              {renderMainSection(props.isColoradoCustomer, true)}
              {currentPage !== PAGE_DUPLICATE_WARNING && (
                <Box css={{ marginTop: 'token(spacing.md) !important' }}>
                  <FaqSection fromDashboard={props.fromDashboard} currentPage={currentPage} />
                </Box>
              )}
              {props.isColoradoCustomer && <CoFeesDisclaimer />}
            </Box>
          </LoadingMask>
        }
      />
    );
  };

  return props.fromDashboard ? renderModal() : renderPage();
};

export default PaymentInfoPage;
